import crypto from 'crypto'
import type {
  QliroCreateOrderRequest,
  QliroCreateOrderResponse,
  QliroOrder,
  QliroUpdateOrderRequest
} from 'ecosystem'
import { createLogger } from 'shared-utils'
import { apiCall, type ApiCallResponse } from '../../fetching'
import { QLIRO_API_KEY, QLIRO_API_URL, QLIRO_API_SECRET } from '../constants'

const ORDERS_URL = `${QLIRO_API_URL}/checkout/merchantapi/orders`

/**
 * Generate Qliro authorization header.
 * Token = base64(sha256(requestBody + merchantApiSecret))
 * @see https://developers.qliro.com/docs/qliro-checkout/get-started/authorization
 */
const createQliroAuthHeader = (requestBody = '') => {
  const hash = crypto
    .createHash('sha256')
    .update(requestBody + QLIRO_API_SECRET, 'utf-8')
    .digest('base64')

  return `Qliro ${hash}`
}

const logger = createLogger('payment', 'qliro')

const qliroApiCall = async <Data>(
  url: string,
  props: RequestInit
): Promise<ApiCallResponse<Data>> => {
  if (url.includes('undefined')) {
    throw new Error('Qliro environment variables missing')
  }

  const body = (props.body as string) ?? ''

  const { data, error, response } = await apiCall<Data>(url, {
    ...props,
    headers: {
      'Content-Type': 'application/json',
      Authorization: createQliroAuthHeader(body),
      ...props.headers
    }
  })

  logger.log('[REQ]', props.method, url, body ? JSON.parse(body) : '')

  if (response && !response.ok) {
    let errorDetail = `Qliro API error (${response.status})`
    try {
      const errorBody = data as Record<string, string> | null
      if (errorBody?.ErrorCode || errorBody?.ErrorMessage) {
        errorDetail = `[${errorBody.ErrorCode}] ${errorBody.ErrorMessage}`
      }
    } catch {
      void 0
    }
    logger.log(`[RES] ${response.status} error with detail`, props.method, url, errorDetail)

    return { data: null, error: errorDetail }
  }

  if (error) {
    logger.log(`[RES] ${response?.status} error`, props.method, url, error)
    return { data: null, error }
  }

  logger.log(`[RES] ${response?.status} success`, props.method, url, data)

  return { data, error: null }
}

/**
 * Create a Qliro Checkout order.
 * @see https://developers.qliro.com/docs/qliro-checkout/checkout
 */
export const createOrder = (payload: QliroCreateOrderRequest) =>
  qliroApiCall<QliroCreateOrderResponse>(ORDERS_URL, {
    method: 'POST',
    body: JSON.stringify(payload)
  })

/**
 * Retrieve an existing Qliro order by OrderId.
 * @see https://developers.qliro.com/docs/api
 */
export const retrieveOrder = (orderId: string | number) =>
  qliroApiCall<QliroOrder>(`${ORDERS_URL}/${orderId}`, {
    method: 'GET'
  })

/**
 * Retrieve an existing Qliro order by MerchantReference.
 */
export const retrieveOrderByReference = (merchantReference: string) =>
  qliroApiCall<QliroOrder>(`${ORDERS_URL}?merchantReference=${merchantReference}`, {
    method: 'GET'
  })

/**
 * Update an existing Qliro Checkout order.
 */
export const updateOrder = (
  orderId: string | number,
  payload: Omit<QliroUpdateOrderRequest, 'MerchantApiKey'>
) =>
  qliroApiCall<QliroOrder>(`${ORDERS_URL}/${orderId}`, {
    method: 'PUT',
    body: JSON.stringify({ ...payload, MerchantApiKey: QLIRO_API_KEY })
  })
